import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { logger } from '../utils/logger';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Globe,
  Monitor,
  Smartphone,
  Tablet,
  Clock,
  Eye,
  MessageCircle,
  Calendar,
  Activity,
  Navigation,
  ExternalLink,
  Languages,
  Wifi,
  Download
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * VisitorDetail Component - Detailed visitor profile page
 */
const VisitorDetail = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Get real-time sessions from AdminLayout's WebSocket stream
  const outletContext = useOutletContext();
  const realtimeSessions = outletContext?.sessions || [];

  // Track if initial fetch is complete to avoid overwriting with stale data
  const initialFetchDone = useRef(false);

  // Timer tick to control time updates every 3 seconds
  const [timeTick, setTimeTick] = useState(0);
  // Memoize current time based on tick to prevent updates between ticks
  const currentTime = React.useMemo(() => Date.now(), [timeTick]);

  // Check if this is a browsing visitor (no session yet)
  const isBrowsingVisitor = sessionId?.startsWith('visitor:');
  const actualVisitorId = isBrowsingVisitor ? sessionId.replace('visitor:', '') : null;

  useEffect(() => {
    fetchVisitorDetails();
  }, [sessionId]);

  // Timer effect for live time updates every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeTick(tick => tick + 1);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Real-time updates: Update session when WebSocket receives new data
  useEffect(() => {
    // Only process real-time updates after initial fetch is complete
    if (!initialFetchDone.current || isBrowsingVisitor || !sessionId) return;

    // Find the current session in the real-time stream
    const updatedSession = realtimeSessions.find(s => s.session_id === sessionId);

    if (updatedSession && session) {
      // Only update if visitor_metadata has changed (contains visited_pages)
      const currentMetadata = JSON.stringify(session.visitor_metadata || {});
      const newMetadata = JSON.stringify(updatedSession.visitor_metadata || {});

      if (currentMetadata !== newMetadata) {
        setSession(prev => ({
          ...prev,
          visitor_metadata: updatedSession.visitor_metadata,
          updated_at: updatedSession.updated_at,
          visitor_active: updatedSession.visitor_active
        }));
      }
    }
  }, [realtimeSessions, sessionId, isBrowsingVisitor, session]);

  const fetchVisitorDetails = async () => {
    try {
      setLoading(true);

      // Get access token for authentication
      const accessToken = localStorage.getItem('access_token');
      if (!accessToken) {
        logger.error('No access token available');
        setLoading(false);
        return;
      }

      const headers = { Authorization: `Bearer ${accessToken}` };

      if (isBrowsingVisitor) {
        // Fetch browsing visitor from active visitors endpoint
        const visitorsRes = await axios.get(`${API}/visitors/active`, { headers });
        const visitor = visitorsRes.data.find(v => v.visitor_id === actualVisitorId);

        if (visitor) {
          // Convert ActiveVisitor to session-like structure for display compatibility
          setSession({
            session_id: null,
            visitor_id: visitor.visitor_id,
            status: 'browsing',
            is_active: visitor.is_active,
            created_at: visitor.created_at,
            updated_at: visitor.last_seen,
            visitor_metadata: visitor.visitor_info || {},
            user_name: null,
            user_email: null,
            user_phone: null,
            message_count: 0,
            _isBrowsing: true
          });
          setMessages([]);
        }
      } else {
        // Fetch regular session with chat history
        const [sessionRes, messagesRes] = await Promise.all([
          axios.get(`${API}/session/${sessionId}`, { headers }),
          axios.get(`${API}/session/${sessionId}/messages`, { headers })
        ]);

        setSession(sessionRes.data);
        setMessages(messagesRes.data || []);
      }
    } catch (error) {
      logger.error('Error fetching visitor details:', error);
    } finally {
      setLoading(false);
      initialFetchDone.current = true;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading visitor details...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">Visitor not found</p>
          <Button onClick={() => navigate('/admin/visitors')} className="mt-4">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const vm = session.visitor_metadata || {};
  // Backend returns user_name, user_email, user_phone as separate fields (not nested in user_data)
  const userData = session.user_data || {
    name: session.user_name,
    email: session.user_email,
    phone: session.user_phone
  };

  const getDeviceIcon = () => {
    const ua = (vm.user_agent || '').toLowerCase();
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
      return Smartphone;
    }
    if (ua.includes('tablet') || ua.includes('ipad')) {
      return Tablet;
    }
    return Monitor;
  };

  const getActivityStatus = () => {
    const lastSeen = new Date(session.updated_at);
    const now = new Date();
    const diffMins = Math.floor((now - lastSeen) / 60000);

    if (diffMins < 5) return { status: 'active', label: 'Active Now', color: 'bg-green-500' };
    if (diffMins < 30) return { status: 'idle', label: 'Idle', color: 'bg-yellow-500' };
    return { status: 'away', label: 'Away', color: 'bg-gray-400' };
  };

  // Get display name for visitor - matches logic in VisitorsTable
  const getVisitorDisplayName = () => {
    const userName = userData.name;

    // If visitor has provided their name, use it
    if (userName) {
      return userName;
    }

    // Check if this is a new visitor (first_visit === true) or returning
    const isNewVisitor = vm.first_visit === true;

    if (isNewVisitor) {
      // For new visitors without name, show "Unnamed Customer"
      return 'Unnamed Customer';
    } else {
      // For returning visitors without name, show "Customer"
      return 'Customer';
    }
  };

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (minutes < 60) return `${minutes}m ${secs}s`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  // Calculate live time on site from current visit start
  const getLiveTimeOnSite = () => {
    const activity = getActivityStatus();
    const visitStart = vm?.current_visit_started_at;

    // Active visitor with known visit start: live counting
    if (activity.status === 'active' && visitStart) {
      const start = new Date(visitStart).getTime();
      const duration = Math.floor((currentTime - start) / 1000);
      return formatDuration(Math.max(0, duration));
    }

    // Idle/Away visitor with known visit start: compute from visit start to last heartbeat
    if (visitStart && session?.updated_at) {
      const start = new Date(visitStart).getTime();
      const end = new Date(session.updated_at).getTime();
      const duration = Math.floor((end - start) / 1000);
      return formatDuration(Math.max(0, duration));
    }

    // Fallback for old sessions without current_visit_started_at
    return formatDuration(vm?.session_duration || 0);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLocation = () => {
    // Use geolocation data from visitor metadata if available
    if (vm.city || vm.country) {
      return {
        country: vm.country || 'Unknown',
        region: vm.region || '',
        city: vm.city || ''
      };
    }

    // Fallback: try to derive from timezone
    const timezone = vm.timezone || '';
    const parts = timezone.split('/');
    return {
      country: parts[0] || 'Unknown',
      region: '',
      city: parts[1]?.replace(/_/g, ' ') || ''
    };
  };

  const DeviceIcon = getDeviceIcon();
  const activity = getActivityStatus();
  const location = getLocation();

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <Button
          onClick={() => navigate('/admin/visitors')}
          variant="outline"
          className="mb-4 flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Visitors
        </Button>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-2xl">
              {getVisitorDisplayName()[0].toUpperCase()}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {getVisitorDisplayName()}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <div className={`w-2 h-2 rounded-full ${activity.color}`}></div>
                <span className="text-sm text-gray-600">{activity.label}</span>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={() => navigate(`/admin/chats?session=${sessionId}`)}
              className="flex items-center gap-2"
            >
              <MessageCircle className="w-4 h-4" />
              View Chat
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Contact & Overview */}
        <div className="space-y-6">
          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {userData.name && (
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500">Name</p>
                    <p className="font-medium">{userData.name}</p>
                  </div>
                </div>
              )}

              {userData.email && (
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="font-medium">{userData.email}</p>
                  </div>
                </div>
              )}

              {userData.phone && (
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500">Phone</p>
                    <p className="font-medium">{userData.phone}</p>
                  </div>
                </div>
              )}

              {!userData.name && !userData.email && !userData.phone && (
                <p className="text-sm text-gray-500 text-center py-4">
                  No contact information provided
                </p>
              )}
            </CardContent>
          </Card>

          {/* Location & Device */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Location & Device
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Location</p>
                  <p className="font-medium">
                    {location.city && location.country !== 'Unknown'
                      ? `${location.city}${location.region ? `, ${location.region}` : ''}, ${location.country}`
                      : location.country}
                  </p>
                  {vm.isp && (
                    <p className="text-xs text-gray-500 mt-1">ISP: {vm.isp}</p>
                  )}
                </div>
              </div>

              {vm.ip_address && (
                <div className="flex items-start gap-3">
                  <Wifi className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500">IP Address</p>
                    <p className="font-medium font-mono text-sm">{vm.ip_address}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <DeviceIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Device</p>
                  <p className="font-medium">
                    {vm.device_type ? vm.device_type.charAt(0).toUpperCase() + vm.device_type.slice(1) : 'Desktop'}
                  </p>
                  {vm.screen_resolution && (
                    <p className="text-xs text-gray-500">{vm.screen_resolution}</p>
                  )}
                </div>
              </div>

              {(vm.os || vm.browser) && (
                <div className="flex items-start gap-3">
                  <Monitor className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500">System & Browser</p>
                    {vm.os && (
                      <p className="font-medium">
                        {vm.os} {vm.os_version && vm.os_version}
                      </p>
                    )}
                    {vm.browser && (
                      <p className="text-sm text-gray-600">
                        {vm.browser} {vm.browser_version && vm.browser_version}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {vm.language && (
                <div className="flex items-start gap-3">
                  <Languages className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500">Language</p>
                    <p className="font-medium">{vm.language}</p>
                  </div>
                </div>
              )}

              {vm.timezone && (
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500">Timezone</p>
                    <p className="font-medium">{vm.timezone}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Session Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Session Stats
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">First Visit</p>
                  <p className="font-medium">{formatDateTime(session.created_at)}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Last Seen</p>
                  <p className="font-medium">{formatDateTime(session.updated_at)}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Eye className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Total Visits</p>
                  <p className="font-medium">{vm.visit_count || 1}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Time on Site</p>
                  <p className="font-medium">{getLiveTimeOnSite()}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <MessageCircle className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Chat Messages</p>
                  <p className="font-medium">{session.message_count || 0}</p>
                </div>
              </div>

              {vm.referrer && (
                <div className="flex items-start gap-3">
                  <Navigation className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500">Came From</p>
                    <p className="font-medium truncate">{vm.referrer}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Activity & Pages */}
        <div className="lg:col-span-2 space-y-6">
          {/* Pages Visited */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Pages Visited ({vm.visited_pages?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {vm.visited_pages && vm.visited_pages.length > 0 ? (
                <div className="relative">
                  {/* Reverse the array to show current page at top */}
                  {[...vm.visited_pages].reverse().map((pageData, index, reversedArray) => {
                    // Handle both old format (string) and new format (object)
                    const pagePath = typeof pageData === 'string' ? pageData : pageData.page;
                    const pageTitle = typeof pageData === 'object' ? pageData.title : null;
                    const timeSpent = typeof pageData === 'object' ? pageData.time_spent : null;
                    const enteredAt = typeof pageData === 'object' ? pageData.entered_at : null;
                    const exitedAt = typeof pageData === 'object' ? pageData.exited_at : null;
                    // Current page is now at index 0 after reversing
                    const isCurrentPage = !exitedAt && index === 0;
                    const isLastItem = index === reversedArray.length - 1;

                    // Calculate display time - for current page, always calculate live from entered_at
                    let displayTime = timeSpent || 0;
                    if (isCurrentPage && enteredAt) {
                      const enteredTime = new Date(enteredAt).getTime();
                      displayTime = Math.floor((currentTime - enteredTime) / 1000);
                    }

                    return (
                      <div
                        key={index}
                        className="relative flex items-start gap-3 pb-4"
                        title={pagePath}
                      >
                        {/* Timeline connector */}
                        <div className="flex flex-col items-center">
                          {/* Bullet point */}
                          <div className={`w-3 h-3 rounded-full border-2 flex-shrink-0 ${
                            isCurrentPage
                              ? 'bg-green-500 border-green-500'
                              : 'bg-white border-gray-300'
                          }`} />
                          {/* Connecting line (not shown for last item) */}
                          {!isLastItem && (
                            <div className="w-0.5 flex-1 bg-gray-200 min-h-[40px]" />
                          )}
                        </div>
                        {/* Page content */}
                        <div className="flex-1 min-w-0 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors -mt-1">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <a
                                href={pagePath}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline block truncate"
                              >
                                {pageTitle || pagePath}
                              </a>
                              <p className="text-xs text-gray-500 mt-1">
                                <Clock className="w-3 h-3 inline mr-1" />
                                {isCurrentPage ? "Currently viewing: " : "Time spent: "}
                                {formatDuration(displayTime)}
                              </p>
                            </div>
                            {isCurrentPage && (
                              <Badge className="bg-green-500 text-white text-xs flex-shrink-0">Current</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  No pages visited yet
                </p>
              )}
            </CardContent>
          </Card>

          {/* Chat History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Chat History ({messages.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {messages.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {messages.map((message, index) => {
                    // Check if this is a system message
                    const isSystemMessage = message.role === 'system' || message.metadata?.action;
                    const isAgentJoin = message.metadata?.action === 'agent_joined';
                    const isAgentLeave = message.metadata?.action === 'agent_left';
                    const isReassignment = message.metadata?.action === 'chat_reassigned';
                    const isUserDataCollected = message.metadata?.action === 'collect_user_data' || message.metadata?.action === 'no_agents_available';

                    // System event messages (agent join/leave, reassignment)
                    if (isSystemMessage || isAgentJoin || isAgentLeave || isReassignment) {
                      return (
                        <div
                          key={index}
                          className="flex items-center justify-center py-2"
                        >
                          <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full text-sm text-gray-600">
                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                            <span className="font-medium">
                              {isAgentJoin && `Agent ${message.metadata?.agent_name || 'Support'} joined the chat`}
                              {isAgentLeave && `Agent ${message.metadata?.agent_name || 'Support'} left the chat`}
                              {isReassignment && `Chat reassigned to ${message.metadata?.agent_name || 'another agent'}`}
                              {!isAgentJoin && !isAgentLeave && !isReassignment && message.content}
                            </span>
                            <span className="text-xs text-gray-400">
                              {formatDateTime(message.timestamp)}
                            </span>
                          </div>
                        </div>
                      );
                    }

                    // Regular chat messages
                    return (
                      <div
                        key={index}
                        className={`p-3 rounded-lg ${
                          message.role === 'user'
                            ? 'bg-blue-50 ml-8'
                            : message.role === 'agent'
                            ? 'bg-green-50 mr-8'
                            : 'bg-gray-50 mr-8'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Badge
                            variant="outline"
                            className={`text-xs ${
                              message.role === 'user'
                                ? 'bg-blue-100'
                                : message.role === 'agent'
                                ? 'bg-green-100'
                                : 'bg-gray-100'
                            }`}
                          >
                            {message.role === 'user' ? 'Visitor' : message.role === 'agent' ? 'Agent' : 'AI'}
                          </Badge>
                          <span className="text-xs text-gray-500">
                            {formatDateTime(message.timestamp)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                          {message.content}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-8">
                  No chat messages yet
                </p>
              )}
            </CardContent>
          </Card>

          {/* Session Details */}
          <Card>
            <CardHeader>
              <CardTitle>Session Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Session ID</p>
                  <code className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                    {session.session_id}
                  </code>
                </div>

                <div>
                  <p className="text-xs text-gray-500 mb-1">Status</p>
                  <Badge variant="outline">{session.status}</Badge>
                </div>

                {session.flow_type && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Flow Type</p>
                    <Badge className="capitalize">{session.flow_type}</Badge>
                  </div>
                )}

                {session.assigned_agent && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Assigned Agent</p>
                    <p className="text-sm font-medium">Agent #{session.assigned_agent.slice(0, 8)}</p>
                  </div>
                )}

                {vm.first_visit !== undefined && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Visitor Type</p>
                    <Badge variant={vm.first_visit ? "default" : "outline"}>
                      {vm.first_visit ? 'First-Time' : 'Returning'}
                    </Badge>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default VisitorDetail;
